import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  MessageSquare, 
  Calendar, 
  Clock, 
  ChevronRight, 
  Search,
  Trash2,
  Download,
  Share2,
  Edit,
  Check,
  X,
  ArrowLeft,
  MoreVertical,
  FileText,
  Code,
  Copy,
  ExternalLink,
  AlertCircle
} from 'lucide-react';
import { API_URL } from './App';
import ReactMarkdown from 'react-markdown';

export default function ChatHistoryPage() {
  const [chats, setChats] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedChat, setSelectedChat] = useState(null);
  const [previewMessages, setPreviewMessages] = useState([]);
  const [loadingPreview, setLoadingPreview] = useState(false);
  const [editingTitle, setEditingTitle] = useState(null);
  const [newTitle, setNewTitle] = useState('');
  const [downloadFormat, setDownloadFormat] = useState('txt');
  const [showShareSuccess, setShowShareSuccess] = useState(false);
  const [shareUrl, setShareUrl] = useState('');
  const navigate = useNavigate();

  useEffect(() => {
    fetchChats();
  }, []);

  const fetchChats = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      if (!token) {
        navigate('/login');
        return;
      }

      const response = await fetch(`${API_URL}/chat/history`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      const data = await response.json();
      if (data.chats) {
        setChats(data.chats);
      }
    } catch (error) {
      console.error('Failed to fetch chats:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadChatPreview = async (chatId) => {
    setLoadingPreview(true);
    setSelectedChat(chatId);
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/chat/history/${chatId}`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      const data = await response.json();
      if (data.chat) {
        setPreviewMessages(data.chat.messages || []);
      }
    } catch (error) {
      console.error('Failed to load chat preview:', error);
    } finally {
      setLoadingPreview(false);
    }
  };

  const deleteChat = async (chatId, e) => {
    e.stopPropagation();
    if (!window.confirm('Are you sure you want to delete this chat?')) return;

    try {
      const token = localStorage.getItem('token');
      await fetch(`${API_URL}/chat/history/${chatId}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      setChats(chats.filter(chat => chat.id !== chatId));
      if (selectedChat === chatId) {
        setSelectedChat(null);
        setPreviewMessages([]);
      }
    } catch (error) {
      console.error('Failed to delete chat:', error);
      alert('Failed to delete chat. Please try again.');
    }
  };

  const updateChatTitle = async (chatId) => {
    if (!newTitle.trim() || newTitle === editingTitle) {
      setEditingTitle(null);
      return;
    }

    try {
      const token = localStorage.getItem('token');
      await fetch(`${API_URL}/chat/history/${chatId}`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ title: newTitle })
      });

      setChats(chats.map(chat => 
        chat.id === chatId ? { ...chat, title: newTitle } : chat
      ));
      setEditingTitle(null);
    } catch (error) {
      console.error('Failed to update chat title:', error);
      alert('Failed to update title. Please try again.');
    }
  };

  const downloadChat = async (chatId, title, format = 'txt') => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/chat/download/${chatId}?format=${format}`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (!response.ok) throw new Error('Download failed');

      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      
      const safeTitle = (title || 'chat').replace(/[^a-z0-9]/gi, '_').toLowerCase();
      a.download = `${safeTitle}_${new Date().getTime()}.${format}`;
      
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      a.remove();
    } catch (error) {
      console.error('Failed to download chat:', error);
      alert('Failed to download chat. Please try again.');
    }
  };

  const shareChat = async (chatId) => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/chat/share/${chatId}`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ expiresIn: 7 })
      });

      const data = await response.json();
      if (data.success) {
        setShareUrl(data.shareUrl);
        setShowShareSuccess(true);
        navigator.clipboard.writeText(data.shareUrl);
        setTimeout(() => setShowShareSuccess(false), 3000);
      }
    } catch (error) {
      console.error('Failed to share chat:', error);
      alert('Failed to create share link. Please try again.');
    }
  };

  const filteredChats = chats.filter(chat =>
    chat.title?.toLowerCase().includes(searchQuery.toLowerCase()) ||
    chat.message_count?.toString().includes(searchQuery)
  );

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffTime = Math.abs(now - date);
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    
    if (diffDays === 0) return 'Today';
    if (diffDays === 1) return 'Yesterday';
    if (diffDays < 7) return `${diffDays} days ago`;
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  };

  if (loading) {
    return (
      <div className="min-h-[calc(100vh-4rem)] flex items-center justify-center">
        <div className="w-8 h-8 border-2 border-purple-500 border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  return (
    <div className="min-h-[calc(100vh-4rem)] p-4">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="mb-6">
          <h1 className="text-2xl font-bold mb-2 flex items-center">
            <MessageSquare className="w-6 h-6 mr-2 text-purple-400" />
            Chat History
          </h1>
          <p className="text-gray-400">
            {chats.length} {chats.length === 1 ? 'conversation' : 'conversations'} saved
          </p>
        </div>

        {/* Share Success Notification */}
        <AnimatePresence>
          {showShareSuccess && (
            <motion.div
              initial={{ opacity: 0, y: -20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -20 }}
              className="mb-6 p-4 bg-green-500/10 border border-green-500/30 rounded-lg"
            >
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-3">
                  <Check className="w-5 h-5 text-green-400" />
                  <div>
                    <p className="font-medium text-green-400">Share link copied!</p>
                    <p className="text-sm text-gray-400">Link expires in 7 days</p>
                  </div>
                </div>
                <button
                  onClick={() => setShowShareSuccess(false)}
                  className="p-1 hover:bg-gray-700/50 rounded"
                >
                  <X className="w-4 h-4" />
                </button>
              </div>
            </motion.div>
          )}
        </AnimatePresence>

        {/* Search Bar */}
        <div className="mb-6">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
            <input
              type="text"
              placeholder="Search conversations by title..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full pl-10 pr-4 py-3 bg-gray-800/50 border border-gray-700 rounded-lg focus:outline-none focus:border-purple-500 text-sm"
            />
          </div>
        </div>

        {chats.length === 0 ? (
          <div className="text-center py-12 bg-gray-800/30 rounded-lg border border-gray-700">
            <MessageSquare className="w-16 h-16 text-gray-600 mx-auto mb-4" />
            <h3 className="text-lg font-medium mb-2">No chat history yet</h3>
            <p className="text-gray-400 mb-6">Start a conversation to see it here</p>
            <Link
              to="/chat"
              className="inline-flex items-center px-6 py-3 bg-purple-600 hover:bg-purple-700 rounded-lg font-medium transition-colors"
            >
              Start Chatting
            </Link>
          </div>
        ) : (
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            {/* Chat List */}
            <div className="lg:col-span-1">
              <div className="bg-gray-800/30 rounded-lg border border-gray-700 p-4">
                <h3 className="text-sm font-medium mb-3">All Conversations</h3>
                <div className="space-y-2 max-h-[calc(100vh-16rem)] overflow-y-auto pr-2">
                  {filteredChats.length === 0 ? (
                    <p className="text-center text-gray-500 py-4">No chats found</p>
                  ) : (
                    filteredChats.map((chat) => (
                      <motion.div
                        key={chat.id}
                        initial={{ opacity: 0, y: 10 }}
                        animate={{ opacity: 1, y: 0 }}
                        className={`group p-3 rounded-lg cursor-pointer transition-colors ${
                          selectedChat === chat.id
                            ? 'bg-purple-600/20 border border-purple-500/50'
                            : 'bg-gray-800 hover:bg-gray-700'
                        }`}
                        onClick={() => loadChatPreview(chat.id)}
                      >
                        <div className="flex items-start justify-between">
                          <div className="flex-1 min-w-0">
                            {editingTitle === chat.id ? (
                              <div className="flex items-center space-x-2 mb-1">
                                <input
                                  type="text"
                                  value={newTitle}
                                  onChange={(e) => setNewTitle(e.target.value)}
                                  onKeyPress={(e) => e.key === 'Enter' && updateChatTitle(chat.id)}
                                  className="flex-1 px-2 py-1 bg-gray-700 border border-purple-500 rounded text-sm focus:outline-none"
                                  autoFocus
                                  onClick={(e) => e.stopPropagation()}
                                />
                                <button
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    updateChatTitle(chat.id);
                                  }}
                                  className="p-1 hover:bg-green-500/20 text-green-400 rounded"
                                >
                                  <Check className="w-3 h-3" />
                                </button>
                                <button
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    setEditingTitle(null);
                                  }}
                                  className="p-1 hover:bg-red-500/20 text-red-400 rounded"
                                >
                                  <X className="w-3 h-3" />
                                </button>
                              </div>
                            ) : (
                              <p className="text-sm font-medium truncate mb-1">
                                {chat.title || 'Untitled Conversation'}
                              </p>
                            )}
                            <div className="flex items-center space-x-2 text-xs text-gray-500">
                              <span>{chat.message_count || 0} messages</span>
                              <span>•</span>
                              <span>{formatDate(chat.updated_at || chat.created_at)}</span>
                            </div>
                          </div>
                          
                          <div className="flex items-center space-x-1 opacity-0 group-hover:opacity-100 transition-opacity">
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                setEditingTitle(chat.id);
                                setNewTitle(chat.title || '');
                              }}
                              className="p-1 hover:bg-gray-600 rounded"
                              title="Edit title"
                            >
                              <Edit className="w-3 h-3" />
                            </button>
                            <button
                              onClick={(e) => deleteChat(chat.id, e)}
                              className="p-1 hover:bg-red-500/20 text-red-400 rounded"
                              title="Delete"
                            >
                              <Trash2 className="w-3 h-3" />
                            </button>
                          </div>
                        </div>
                      </motion.div>
                    ))
                  )}
                </div>
              </div>
            </div>

            {/* Chat Preview */}
            <div className="lg:col-span-2">
              {selectedChat ? (
                <div className="bg-gray-800/30 rounded-lg border border-gray-700 p-6">
                  {loadingPreview ? (
                    <div className="flex justify-center py-12">
                      <div className="w-8 h-8 border-2 border-purple-500 border-t-transparent rounded-full animate-spin"></div>
                    </div>
                  ) : (
                    <>
                      {/* Preview Header */}
                      <div className="flex items-start justify-between mb-6">
                        <div>
                          <h2 className="text-lg font-semibold mb-2 line-clamp-2">
                            {chats.find(c => c.id === selectedChat)?.title || 'Chat Preview'}
                          </h2>
                          <div className="flex items-center space-x-4 text-sm text-gray-400">
                            <div className="flex items-center">
                              <Calendar className="w-4 h-4 mr-1" />
                              {formatDate(chats.find(c => c.id === selectedChat)?.created_at)}
                            </div>
                            <div className="flex items-center">
                              <Clock className="w-4 h-4 mr-1" />
                              {previewMessages.length} messages
                            </div>
                          </div>
                        </div>
                        
                        <div className="flex items-center space-x-2">
                          {/* Download Dropdown */}
                          <div className="relative group">
                            <button
                              className="p-2 bg-gray-800 hover:bg-gray-700 rounded-lg transition-colors"
                              title="Download"
                            >
                              <Download className="w-4 h-4" />
                            </button>
                            <div className="absolute right-0 mt-2 w-48 bg-gray-800 border border-gray-700 rounded-lg shadow-xl opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all z-50">
                              <button
                                onClick={() => downloadChat(selectedChat, chats.find(c => c.id === selectedChat)?.title, 'txt')}
                                className="flex items-center space-x-2 w-full p-3 hover:bg-gray-700 rounded-t-lg text-left"
                              >
                                <FileText className="w-4 h-4" />
                                <span>Download as Text</span>
                              </button>
                              <button
                                onClick={() => downloadChat(selectedChat, chats.find(c => c.id === selectedChat)?.title, 'json')}
                                className="flex items-center space-x-2 w-full p-3 hover:bg-gray-700 rounded-b-lg text-left"
                              >
                                <Code className="w-4 h-4" />
                                <span>Download as JSON</span>
                              </button>
                            </div>
                          </div>
                          
                          <button
                            onClick={() => shareChat(selectedChat)}
                            className="p-2 bg-gray-800 hover:bg-gray-700 rounded-lg transition-colors"
                            title="Share"
                          >
                            <Share2 className="w-4 h-4" />
                          </button>
                          
                          <Link
                            to={`/chat?id=${selectedChat}`}
                            className="p-2 bg-purple-600 hover:bg-purple-700 rounded-lg transition-colors"
                            title="Continue chat"
                          >
                            <MessageSquare className="w-4 h-4" />
                          </Link>
                        </div>
                      </div>

                      {/* Messages Preview */}
                      <div className="space-y-4 max-h-[500px] overflow-y-auto pr-2">
                        {previewMessages.slice(-5).map((msg, index) => (
                          <div
                            key={index}
                            className={`flex gap-3 ${msg.role === 'user' ? 'justify-end' : ''}`}
                          >
                            <div className={`max-w-[80%] ${msg.role === 'user' ? 'order-first' : ''}`}>
                              <div className={`p-3 rounded-lg ${
                                msg.role === 'user'
                                  ? 'bg-gray-800'
                                  : 'bg-gray-800/50'
                              }`}>
                                <p className="text-xs text-gray-400 mb-1">
                                  {msg.role === 'user' ? 'You' : 'AI Genesis'}
                                </p>
                                <ReactMarkdown className="prose prose-invert max-w-none text-sm line-clamp-3">
                                  {msg.content}
                                </ReactMarkdown>
                              </div>
                              {msg.timestamp && (
                                <p className="text-xs text-gray-500 mt-1">
                                  {new Date(msg.timestamp).toLocaleTimeString([], { 
                                    hour: '2-digit', 
                                    minute: '2-digit' 
                                  })}
                                </p>
                              )}
                            </div>
                          </div>
                        ))}
                        
                        {previewMessages.length > 5 && (
                          <div className="text-center text-sm text-gray-500">
                            + {previewMessages.length - 5} more messages
                          </div>
                        )}
                      </div>
                    </>
                  )}
                </div>
              ) : (
                <div className="bg-gray-800/30 rounded-lg border border-gray-700 p-12 text-center">
                  <MessageSquare className="w-16 h-16 text-gray-600 mx-auto mb-4" />
                  <h3 className="text-lg font-medium mb-2">Select a conversation</h3>
                  <p className="text-gray-400">Choose a chat from the list to view the preview</p>
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}